<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  available: {
    type: Array,
    default: () => [],
  },
  multi: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  maxVisible: {
    type: Number,
    default: 5,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();

const comboboxOpen = ref(false);
const searchQuery = ref('');
const containerRef = ref(null);
const searchRef = ref(null);

// ── Derived ──────────────────────────────────────────────────────────────────

const selectedUsers = computed(() =>
  props.modelValue
    .map(id => props.available.find(u => u.id === id))
    .filter(Boolean)
);

const visibleUsers = computed(() =>
  selectedUsers.value.slice(0, props.maxVisible)
);

const overflowCount = computed(() =>
  Math.max(0, selectedUsers.value.length - props.maxVisible)
);

const filteredAvailable = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return props.available.filter(u => {
    if (!q) return true;
    return (
      u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  });
});

const effectivePlaceholder = computed(
  () => props.placeholder || t('PIPELINE.INLINE_EDIT.ADD_AGENT')
);

// ── Interactions ─────────────────────────────────────────────────────────────

const openCombobox = () => {
  if (props.disabled) return;
  searchQuery.value = '';
  comboboxOpen.value = true;
  setTimeout(() => searchRef.value?.focus(), 50);
};

const closeCombobox = () => {
  comboboxOpen.value = false;
  searchQuery.value = '';
};

const removeUser = userId => {
  if (props.disabled) return;
  const prev = [...props.modelValue];
  const next = prev.filter(id => id !== userId);
  emit('update:modelValue', next);
  emit('change', next, prev);
};

const addUser = user => {
  const prev = [...props.modelValue];
  let next;

  if (!props.multi) {
    next = [user.id];
  } else if (prev.includes(user.id)) {
    next = prev.filter(id => id !== user.id);
  } else {
    next = [...prev, user.id];
  }

  emit('update:modelValue', next);
  emit('change', next, prev);

  if (!props.multi) closeCombobox();
};

const isSelected = userId => props.modelValue.includes(userId);

const onKeydown = e => {
  if (e.key === 'Escape') closeCombobox();
};

// ── Click-outside ────────────────────────────────────────────────────────────

const onDocumentClick = e => {
  if (!comboboxOpen.value) return;
  if (containerRef.value && !containerRef.value.contains(e.target)) {
    closeCombobox();
  }
};

onMounted(() => document.addEventListener('click', onDocumentClick, true));
onBeforeUnmount(() =>
  document.removeEventListener('click', onDocumentClick, true)
);

// ── Helpers ──────────────────────────────────────────────────────────────────

const userStatus = user => {
  if (user.online === true) return 'online';
  return null;
};

// Expose for testing
defineExpose({ comboboxOpen, onKeydown, closeCombobox, openCombobox });
</script>

<template>
  <div
    ref="containerRef"
    class="relative inline-flex flex-wrap items-center gap-2"
    @keydown="onKeydown"
  >
    <!-- ── Selected users list ── -->
    <div
      v-for="user in visibleUsers"
      :key="user.id"
      class="inline-flex items-center gap-1.5 bg-n-alpha-1 rounded-full pl-1 pr-2 py-0.5"
    >
      <Avatar
        :name="user.name"
        :src="user.avatar_url"
        :size="22"
        rounded-full
        :status="userStatus(user)"
      />
      <span class="text-xs text-n-slate-12 font-medium max-w-[100px] truncate">
        {{ user.name }}
      </span>
      <button
        v-if="!disabled"
        type="button"
        :aria-label="t('PIPELINE.INLINE_EDIT.REMOVE_AGENT')"
        class="text-n-slate-10 hover:text-n-slate-12 transition-colors duration-75 flex-shrink-0"
        @click.stop="removeUser(user.id)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>

    <!-- Overflow badge -->
    <span
      v-if="overflowCount > 0"
      class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-n-alpha-2 text-xs text-n-slate-11 font-medium"
    >
      +{{ overflowCount }}
    </span>

    <!-- Empty state label -->
    <span
      v-if="selectedUsers.length === 0 && !comboboxOpen"
      class="text-xs text-n-slate-10 italic"
    >
      {{ t('PIPELINE.INLINE_EDIT.EMPTY_LIST') }}
    </span>

    <!-- ── Add button ── -->
    <button
      v-if="!disabled"
      type="button"
      class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs text-n-slate-11 border border-dashed border-n-weak hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-100"
      @click.stop="openCombobox"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      {{ effectivePlaceholder }}
    </button>

    <!-- ── Combobox dropdown ── -->
    <div
      v-if="comboboxOpen"
      class="absolute top-full left-0 mt-1 z-50 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg p-2 min-w-[280px] max-h-[320px] overflow-auto"
    >
      <!-- Search input -->
      <div class="mb-2">
        <input
          ref="searchRef"
          v-model="searchQuery"
          type="search"
          :placeholder="t('PIPELINE.INLINE_EDIT.SEARCH_USERS')"
          class="w-full rounded-md px-3 py-1.5 text-sm text-n-slate-12 bg-n-solid-2 border border-n-weak focus:border-n-brand focus:ring-1 focus:ring-n-brand/20 focus:outline-none"
          @keydown.stop
        />
      </div>

      <!-- Results list -->
      <div v-if="filteredAvailable.length > 0" role="listbox">
        <button
          v-for="user in filteredAvailable"
          :key="user.id"
          role="option"
          type="button"
          :aria-selected="isSelected(user.id)"
          class="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-n-alpha-1 transition-colors duration-75 cursor-pointer"
          :class="isSelected(user.id) ? 'bg-n-alpha-1' : ''"
          @click.stop="addUser(user)"
        >
          <Avatar
            :name="user.name"
            :src="user.avatar_url"
            :size="28"
            rounded-full
            :status="userStatus(user)"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ user.name }}
            </p>
            <p v-if="user.email" class="text-xs text-n-slate-10 truncate">
              {{ user.email }}
            </p>
          </div>

          <!-- Selected checkmark -->
          <svg
            v-if="isSelected(user.id)"
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-n-brand flex-shrink-0"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      </div>

      <!-- No results -->
      <p v-else class="text-xs text-n-slate-10 text-center py-3">
        {{ t('PIPELINE.INLINE_EDIT.NO_USERS_FOUND') }}
      </p>
    </div>
  </div>
</template>
